import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

import Header from "../components/Header";
import UpgradeModal from "../components/UpgradeModal";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Tutorial Library — the marquee Pro feature page.
 *
 * Free users see the same tile grid as Pro users, but tapping a tile
 * pops the paywall modal instead of starting generation. Showing what's
 * behind the wall is intentional — it converts better than hiding it.
 *
 * Each tile is a "vibe": everyday · glam · drag · transformation ·
 * skill-building · event · trend recreation. Tapping calls
 * `POST /api/tutorials/library/generate` which (for Pro users) returns
 * a personalised routine seeded by the user's most-recent face scan.
 *
 * Today the backend returns `status: "coming_soon"` for the actual
 * generation; the frontend renders a graceful holding state instead of
 * a routine. When the backend swaps in real GPT-4o output, this page
 * will route to /routine/:id with no other change.
 */
const TutorialLibrary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vibes, setVibes] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: lib }, { data: u }] = await Promise.all([
          axios.get(`${API}/tutorials/library`),
          axios.get(`${API}/me/usage`, { withCredentials: true }),
        ]);
        if (!alive) return;
        setVibes(lib.vibes || []);
        setUsage(u);
      } catch {
        if (alive) toast.error("Couldn't load the library — try again.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const isPro =
    usage?.tier === "pro" || usage?.is_founding_member;

  const onTileClick = async (vibe) => {
    if (!user) {
      navigate("/login?next=/tutorials/library");
      return;
    }
    if (!isPro) {
      setShowPaywall(true);
      return;
    }
    setGenerating(vibe.id);
    try {
      const { data } = await axios.post(
        `${API}/tutorials/library/generate`,
        { vibe_id: vibe.id },
        { withCredentials: true },
      );
      if (data.status === "coming_soon") {
        toast.success(data.message, { duration: 5000 });
      } else {
        // Future: backend will return a routine_id; we'll route there.
        navigate("/my-routines");
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowPaywall(true);
      } else if (e?.response?.status === 409) {
        toast.info(
          "You'll need a recent face scan first — let's get one going.",
        );
        navigate("/capture");
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't generate.");
      }
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF6F1] via-[#FAF3EE] to-[#F5EBE3]">
      <Header />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <div className="mb-10 sm:mb-14">
          <div className="text-[#9B6A6C] text-xs uppercase tracking-[0.25em] mb-3">
            Tutorial Library
          </div>
          <h1
            className="font-serif text-4xl sm:text-5xl text-[#2C2123] leading-tight mb-3"
            data-testid="library-h1"
          >
            Your drag mom,{" "}
            <em className="italic text-[#5D4050]">on every mood.</em>
          </h1>
          <p className="text-[#5C4B51] text-base sm:text-lg max-w-2xl">
            Personalised, full-routine tutorials generated on-demand for the
            face she already knows. Pick a vibe — she'll write the steps.
          </p>
          {!isPro && !loading && (
            <div className="mt-5 inline-flex items-center gap-2 bg-[#9B6A6C]/10 text-[#5D4050] border border-[#9B6A6C]/30 rounded-full px-4 py-2 text-sm">
              <Lock className="h-3.5 w-3.5" />
              Library tutorials unlock with TruBeauté Pro
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 text-[#9B6A6C] animate-spin" />
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="library-grid"
          >
            {vibes.map((v) => (
              <button
                key={v.id}
                onClick={() => onTileClick(v)}
                disabled={generating === v.id}
                className="group relative text-left bg-white rounded-2xl border border-[#EADDD7] p-6 hover:border-[#9B6A6C]/50 hover:shadow-lg transition-all disabled:opacity-60"
                data-testid={`library-tile-${v.id}`}
              >
                {!isPro && (
                  <span className="absolute top-4 right-4 inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#9B6A6C]/15 text-[#5D4050]">
                    <Lock className="h-3.5 w-3.5" />
                  </span>
                )}
                <div className="text-[#9B6A6C] text-[10px] uppercase tracking-[0.2em] mb-2">
                  Vibe · {v.id}
                </div>
                <h3 className="font-serif text-2xl text-[#2C2123] mb-2">
                  {v.label}
                </h3>
                <p className="text-[#5C4B51] text-sm leading-relaxed">
                  {v.tagline}
                </p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs text-[#9B6A6C] group-hover:text-[#5D4050]">
                  {generating === v.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  {generating === v.id ? "Calling her in…" : "Generate for me"}
                </div>
              </button>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-[#8A7D82] mt-12 italic">
          Want a specific tutorial you don't see here? Send a request →
          <button
            onClick={() => {
              if (!isPro) {
                setShowPaywall(true);
                return;
              }
              navigate("/tutorials/request");
            }}
            className="underline ml-1 hover:text-[#5D4050]"
            data-testid="library-request-link"
          >
            Request a tutorial
          </button>
          .
        </p>
      </main>

      <UpgradeModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        used={usage?.tutorial_views_count || 0}
        limit={usage?.tutorial_limit || 3}
      />
    </div>
  );
};

export default TutorialLibrary;
